import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import type { User } from '@wml-insta/shared';
import { api, setToken } from '../api/client.js';
import { SelfPasswordModal } from './SelfPasswordModal.js';

interface Props {
  user: User;
  isSuperAdmin: boolean;
}

function firstLetter(s: string): string {
  const c = s.trim().charAt(0);
  return c ? c.toUpperCase() : '?';
}

export function Sidebar({ user, isSuperAdmin }: Props) {
  // Счётчик «на выключение» — виден без захода на вкладку, обновляется поллом.
  const [shutdownCount, setShutdownCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const tick = () => {
      api<{ count: number }>('/api/shutdown/candidates?countOnly=1')
        .then((r) => { if (alive) setShutdownCount(r.count); })
        .catch(() => {});
    };
    tick();
    const t = setInterval(tick, 120_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const [pwOpen, setPwOpen] = useState(false);

  return (
    <aside className="sidebar">
      <Link to="/" className="sidebar-brand" title="WML Software · на главную">
        <div className="brand-logo-full" role="img" aria-label="WML Software" />
      </Link>

      <div className="nav-section">Панель</div>
      <NavLink to="/" end className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="9" rx="1.5" />
          <rect x="14" y="3" width="7" height="5" rx="1.5" />
          <rect x="14" y="12" width="7" height="9" rx="1.5" />
          <rect x="3" y="16" width="7" height="5" rx="1.5" />
        </svg>
        Дашборд
      </NavLink>
      <NavLink to="/phones" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="6" y="2" width="12" height="20" rx="2.5" />
          <path d="M11 18h2" />
        </svg>
        Телефоны
      </NavLink>
      <NavLink to="/models" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
        </svg>
        Модели
      </NavLink>
      <NavLink to="/accounts" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 6h18M3 12h18M3 18h18" />
        </svg>
        Все аккаунты
      </NavLink>

      {user.role === 'admin' && (
        <NavLink to="/analytics" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3v18h18" />
            <path d="M7 14l4-4 3 3 5-6" />
          </svg>
          Аналитика
        </NavLink>
      )}

      <NavLink to="/shutdown" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
          <path d="M12 2v10" />
        </svg>
        На выключение
        {shutdownCount > 0 && (
          <span style={{
            marginLeft: 'auto', background: 'var(--accent)', color: '#fff',
            borderRadius: 999, fontSize: 11, fontWeight: 700, lineHeight: 1,
            padding: '3px 7px', minWidth: 18, textAlign: 'center',
          }}>{shutdownCount > 99 ? '99+' : shutdownCount}</span>
        )}
      </NavLink>

      <NavLink to="/coordinates" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
        Координаты
      </NavLink>

      <NavLink to="/archive" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="4" rx="1" />
          <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
          <path d="M10 12h4" />
        </svg>
        Архив
      </NavLink>

      {isSuperAdmin && (
        <>
          <div className="nav-section">Управление</div>
          <NavLink to="/admin" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l3 6 6.5 1-4.75 4.75L18 21l-6-3.25L6 21l1.25-7.25L2.5 9 9 8z" />
            </svg>
            Админка
          </NavLink>
        </>
      )}

      <div className="sidebar-footer">
        <div className="avatar">{firstLetter(user.name)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="user-name">{user.name}</div>
          <div className="user-email">{user.email}</div>
        </div>
        <button
          type="button"
          className="sidebar-pw-btn"
          title="Сменить пароль"
          onClick={() => setPwOpen(true)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="8" cy="15" r="4" />
            <path d="m10.85 12.15 8.15-8.15" />
            <path d="m18 5 3 3" />
            <path d="m15 8 3 3" />
          </svg>
        </button>
      </div>

      {pwOpen && (
        <SelfPasswordModal
          onClose={() => setPwOpen(false)}
          onChanged={(nextToken) => setToken(nextToken)}
        />
      )}
    </aside>
  );
}
